// Draft suggestions. Accept posts a new plan id. It does not write to Meta.
// A 409 means this suggestion was already accepted, so the button stays closed.

import { useState } from 'react';
import { Button, Text, VStack } from '@chakra-ui/react';
import type { AcceptSuggestionResult, PlanSuggestionView } from '../../marketingPlans/drafts';

export function SuggestionsList({
  suggestions, onAccept
}: {
  suggestions: PlanSuggestionView[] | null;
  onAccept?: (suggestion: PlanSuggestionView) => Promise<AcceptSuggestionResult>;
}) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [closedIds, setClosedIds] = useState<string[]>([]);

  if (suggestions === null) {
    return <Text fontSize="sm" color="brand.muted" data-testid="suggestions-unavailable">Suggestions aren't available yet.</Text>;
  }
  if (suggestions.length === 0) {
    return <Text fontSize="sm" color="brand.muted" data-testid="suggestions-empty">No suggestions yet.</Text>;
  }

  const accept = async (item: PlanSuggestionView) => {
    if (!onAccept || !item.id || pendingId || closedIds.includes(item.id) || item.status === 'accepted' || item.status === 'dismissed') return;
    setPendingId(item.id);
    const result = await onAccept(item);
    setPendingId(null);
    if (result.duplicate) {
      setClosedIds((ids) => [...ids, item.id as string]);
      setNotes((prev) => ({ ...prev, [item.id as string]: 'Already accepted.' }));
      return;
    }
    if (result.planId) {
      setClosedIds((ids) => [...ids, item.id as string]);
      setNotes((prev) => ({ ...prev, [item.id as string]: `New plan ${result.planId}` }));
      return;
    }
    if (result.error) setNotes((prev) => ({ ...prev, [item.id as string]: result.error as string }));
  };

  return (
    <VStack align="stretch" spacing={2} data-testid="plan-suggestions">
      {suggestions.map((item, index) => {
        const canAccept = Boolean(
          onAccept && item.id && item.status !== 'accepted' && item.status !== 'dismissed' && !closedIds.includes(item.id)
        );
        return (
          <VStack key={`${item.id ?? item.kind ?? 'suggestion'}-${index}`} align="stretch" spacing={0}>
            <Text fontSize="sm" fontWeight="700">
              {[item.kind, item.status].filter(Boolean).join(' · ')}
            </Text>
            {item.reason && <Text fontSize="sm">{item.reason}</Text>}
            {item.effect && <Text fontSize="xs" color="brand.muted">Expected effect: {item.effect}</Text>}
            {item.id && notes[item.id] && <Text fontSize="xs" color="brand.muted" data-testid="suggestion-note">{notes[item.id]}</Text>}
            {canAccept && (
              <Button
                size="xs"
                variant="outline"
                alignSelf="flex-start"
                mt={1}
                isLoading={pendingId === item.id}
                isDisabled={pendingId != null}
                data-testid={`accept-suggestion-${item.id}`}
                onClick={() => { void accept(item); }}
              >
                Accept
              </Button>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
}
